import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { comunidadAPI, uploadsAPI, hubError } from '../lib/api';
import { TopBar } from '../components/layout/TopBar';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Church, Plus, Edit, Trash2, Loader2, Upload, CheckCircle2, Info, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const emptyForm = { name: '', description: '', category: '', schedule: '', image_url: '', order_index: 100 };

export default function ComunidadPage() {
  const { setMobileMenuOpen, churchInfo } = useOutletContext();
  const [config, setConfig] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [confirmPublish, setConfirmPublish] = useState(null);
  const [uploading, setUploading] = useState(false);

  const uploadImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadsAPI.image(file, undefined, 'ministries');
      const url = res.data?.data?.url || res.data?.url;
      if (!url) throw new Error('sin url');
      setForm(prev => ({ ...prev, image_url: url }));
      toast.success('Imagen lista');
    } catch (err) { toast.error(hubError(err, 'No se pudo subir la imagen')); }
    finally { setUploading(false); event.target.value = ''; }
  };
  const [accessError, setAccessError] = useState(null);

  const fetchAll = useCallback(() => {
    setLoading(true);
    Promise.all([comunidadAPI.config(), comunidadAPI.ministries()])
      .then(([cfg, mins]) => {
        setAccessError(null);
        setConfig(cfg.data?.data || null);
        setDrafts(mins.data?.data || []);
      })
      .catch(err => setAccessError(hubError(err, 'No se pudo cargar la Comunidad')))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleFlag = async (key, value) => {
    if (config?.[key] === value) return;
    setConfig(prev => ({ ...prev, [key]: value }));
    try {
      const res = await comunidadAPI.updateConfig({ [key]: value });
      setConfig(res.data?.data || null);
    } catch (err) {
      toast.error(hubError(err, 'No se pudo guardar el cambio'));
      fetchAll();
    }
  };

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (item) => {
    setEditing(item.id);
    setForm({
      name: item.name || '', description: item.description || '',
      category: item.category || '', schedule: item.schedule || '',
      image_url: item.image_url || '',
      order_index: item.order_index ?? 100,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (form.name.trim().length < 2) { toast.error('Escribe el nombre del ministerio'); return; }
    if (form.image_url && !/^https:\/\/.+/.test(form.image_url.trim())) {
      toast.error('La foto debe ser un enlace que empiece por https://');
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      description: form.description,
      category: form.category || null,
      schedule: form.schedule || null,
      image_url: form.image_url.trim() || null,
      order_index: Number(form.order_index) || 100,
    };
    try {
      if (editing) await comunidadAPI.updateMinistry(editing, payload);
      else await comunidadAPI.createMinistry(payload);
      toast.success(editing ? 'Borrador actualizado' : 'Borrador creado');
      setDialogOpen(false);
      fetchAll();
    } catch (err) { toast.error(hubError(err, 'Error al guardar')); }
    finally { setSaving(false); }
  };

  const publish = async (item) => {
    setConfirmPublish(null);
    setPublishing(item.id);
    try {
      await comunidadAPI.publishMinistry(item.id);
      toast.success(`${item.name} ya está en la congregación`);
      fetchAll();
    } catch (err) { toast.error(hubError(err, 'No se pudo publicar en el Hub')); }
    finally { setPublishing(null); }
  };

  const confirmDelete = async () => {
    try {
      await comunidadAPI.deleteMinistry(deleting.id);
      toast.success('Borrador eliminado');
      setDeleting(null);
      fetchAll();
    } catch (err) { toast.error(hubError(err, 'No se pudo eliminar')); }
  };

  const pending = drafts.filter(d => d.status !== 'published').length;

  return (
    <div className="min-h-screen bg-background" data-testid="comunidad-page">
      <TopBar title="Comunidad Tu Iglesia" subtitle="Congregación propia de la plataforma"
        churchInfo={churchInfo} onMenuClick={() => setMobileMenuOpen(true)} />

      <div className="p-4 lg:p-8 max-w-5xl">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 spinner text-[#056A84]" /></div>
        ) : accessError ? (
          <Card className="border-red-200" data-testid="comunidad-access-error">
            <CardContent className="p-6 text-center">
              <Info className="w-5 h-5 text-red-500 mx-auto mb-2" />
              <p className="text-sm text-red-700">{accessError}</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card className="mb-6 border-[#056A84]/20">
              <CardContent className="p-5 space-y-5">
                <div className="flex items-start gap-3">
                  <Info className="w-4 h-4 text-[#056A84] mt-1 shrink-0" />
                  <p className="text-sm text-muted-foreground">
                    Esta comunidad no funciona como una congregación regular: no tiene camino al
                    bautismo y la lleva un administrador de nuestro lado, no un pastor.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Responsable visible para los miembros</Label>
                  <Input
                    value={config?.leader_label || ''}
                    onChange={(e) => setConfig({ ...config, leader_label: e.target.value })}
                    onBlur={(e) => toggleFlag('leader_label', e.target.value.trim() || 'Administración Tu Iglesia')}
                    data-testid="comunidad-leader-label"
                  />
                  <p className="text-xs text-muted-foreground">Se muestra en lugar del nombre de un pastor.</p>
                </div>

                <div className="flex items-center justify-between gap-3 rounded-lg border p-3">
                  <div>
                    <Label className="text-sm">Camino al bautismo</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Desactivado: la comunidad no prepara ni agenda bautismos.
                    </p>
                  </div>
                  <Switch checked={!!config?.baptism_path_enabled}
                    onCheckedChange={(v) => toggleFlag('baptism_path_enabled', v)}
                    data-testid="comunidad-baptism-toggle" />
                </div>

                <div className="flex items-center justify-between gap-3 rounded-lg border p-3">
                  <div>
                    <Label className="text-sm">Semillero: conectar con una iglesia cercana</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Pendiente de activar. Cuando lo enciendas, la comunidad podrá derivar a
                      quien quiera dar el paso presencial.
                    </p>
                  </div>
                  <Switch checked={!!config?.seedbed_enabled}
                    onCheckedChange={(v) => toggleFlag('seedbed_enabled', v)}
                    data-testid="comunidad-seedbed-toggle" />
                </div>
              </CardContent>
            </Card>

            <div className="flex items-center justify-between gap-3 mb-3">
              <div>
                <h2 className="text-base md:text-lg font-semibold">Ministerios</h2>
                <p className="text-xs text-muted-foreground">
                  {pending > 0
                    ? `${pending} en borrador: revísalos y publícalos cuando estén listos.`
                    : 'Todos los ministerios están publicados en la congregación.'}
                </p>
              </div>
              <Button size="sm" className="bg-[#056A84] hover:bg-[#045A70] text-white"
                onClick={openCreate} data-testid="comunidad-new-ministry">
                <Plus className="w-4 h-4 mr-1" /> Nuevo
              </Button>
            </div>

            {config && config.can_publish === false && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 mb-3"
                data-testid="comunidad-publish-blocked">
                <Info className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-800">
                  Tu sesión pertenece a {config.session_church || 'otra congregación'}, así que el Hub
                  crearía el ministerio allí. Para publicar hace falta una cuenta de Comunidad Tu Iglesia.
                </p>
              </div>
            )}

            <div className="space-y-3">
              {drafts.map(item => (
                <Card key={item.id} data-testid={`comunidad-ministry-${item.id}`}>
                  <CardContent className="p-4 flex flex-col sm:flex-row sm:items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-[#056A84]/10 flex items-center justify-center shrink-0 overflow-hidden">
                      {item.image_url
                        ? <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                        : <Church className="w-4 h-4 text-[#056A84]" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-medium">{item.name}</h3>
                        {item.status === 'published' ? (
                          <Badge className="text-[10px] bg-green-100 text-green-700 hover:bg-green-100">Publicado</Badge>
                        ) : (
                          <Badge className="text-[10px] bg-amber-100 text-amber-800 hover:bg-amber-100">Borrador</Badge>
                        )}
                        {item.category && <Badge variant="secondary" className="text-[10px]">{item.category}</Badge>}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
                      {item.schedule && <p className="text-xs text-muted-foreground mt-1">{item.schedule}</p>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {item.status === 'published' ? (
                        <span className="flex items-center text-xs text-green-700">
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> En el Hub
                        </span>
                      ) : (
                        <>
                          <Button size="sm" variant="outline" onClick={() => openEdit(item)}
                            data-testid={`comunidad-edit-${item.id}`}>
                            <Edit className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                            onClick={() => setDeleting(item)} data-testid={`comunidad-delete-${item.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" className="bg-[#056A84] hover:bg-[#045A70] text-white"
                            disabled={publishing === item.id || config?.can_publish === false}
                            title={config?.can_publish === false
                              ? 'Necesitas una cuenta de Comunidad Tu Iglesia para publicar' : undefined}
                            onClick={() => setConfirmPublish(item)} data-testid={`comunidad-publish-${item.id}`}>
                            {publishing === item.id
                              ? <Loader2 className="w-3.5 h-3.5 spinner" />
                              : <><Upload className="w-3.5 h-3.5 mr-1" /> Publicar</>}
                          </Button>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {config?.segments?.length > 0 && (
              <Card className="mt-8">
                <CardContent className="p-5">
                  <h2 className="text-base md:text-lg font-semibold mb-1">Segmentos del feed</h2>
                  <p className="text-xs text-muted-foreground mb-4">
                    Los siete segmentos de la guía editorial que alimentarán las noticias de la comunidad.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {config.segments.map(s => (
                      <div key={s.key} className="rounded-lg border p-3" data-testid={`comunidad-segment-${s.key}`}>
                        <p className="text-sm font-medium">{s.label}</p>
                        <p className="text-xs text-muted-foreground">{s.frequency} · {s.length}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="comunidad-ministry-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar borrador' : 'Nuevo ministerio'}</DialogTitle>
            <DialogDescription>
              Queda como borrador hasta que lo publiques en la congregación.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="comunidad-form-name" />
            </div>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Textarea rows={4} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="comunidad-form-description" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Categoría</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                  data-testid="comunidad-form-category" />
              </div>
              <div className="space-y-2">
                <Label>Ritmo o horario</Label>
                <Input value={form.schedule} onChange={(e) => setForm({ ...form, schedule: e.target.value })}
                  data-testid="comunidad-form-schedule" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Foto del ministerio</Label>
              <div className="flex items-center gap-3">
                {form.image_url ? (
                  <img src={form.image_url} alt="Vista previa"
                    className="w-16 h-16 rounded-lg object-cover border"
                    data-testid="comunidad-form-image-preview" />
                ) : (
                  <div className="w-16 h-16 rounded-lg border border-dashed flex items-center justify-center">
                    <ImageIcon className="w-5 h-5 text-muted-foreground" />
                  </div>
                )}
                <div className="flex-1 space-y-2">
                  <Input value={form.image_url} placeholder="https://… o sube una imagen"
                    onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                    data-testid="comunidad-form-image-url" />
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-[#056A84] underline cursor-pointer">
                      {uploading ? 'Subiendo…' : 'Subir imagen'}
                      <input type="file" accept="image/*" className="hidden" disabled={uploading}
                        onChange={uploadImage} data-testid="comunidad-form-image-file" />
                    </label>
                    {form.image_url && (
                      <button type="button" className="text-xs text-red-600 underline"
                        onClick={() => setForm({ ...form, image_url: '' })}
                        data-testid="comunidad-form-image-clear">Quitar</button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button className="bg-[#056A84] hover:bg-[#045A70] text-white" onClick={save}
              disabled={saving} data-testid="comunidad-form-save">
              {saving ? <Loader2 className="w-4 h-4 spinner mr-1" /> : null} Guardar borrador
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleting} onOpenChange={(o) => { if (!o) setDeleting(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="comunidad-delete-dialog">
          <DialogHeader>
            <DialogTitle>Eliminar borrador</DialogTitle>
            <DialogDescription>
              Se quitará «{deleting?.name}» de la lista. No afecta a la congregación porque todavía no está publicado.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>Cancelar</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={confirmDelete}
              data-testid="comunidad-delete-confirm">Eliminar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!confirmPublish} onOpenChange={(o) => { if (!o) setConfirmPublish(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="comunidad-publish-dialog">
          <DialogHeader>
            <DialogTitle>Publicar en la congregación</DialogTitle>
            <DialogDescription>
              «{confirmPublish?.name}» quedará visible para los miembros de Comunidad Tu Iglesia
              en la app. Esta acción escribe en la congregación real.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmPublish(null)}
              data-testid="comunidad-publish-cancel">Cancelar</Button>
            <Button className="bg-[#056A84] hover:bg-[#045A70] text-white"
              onClick={() => publish(confirmPublish)} data-testid="comunidad-publish-confirm">
              <Upload className="w-4 h-4 mr-1" /> Publicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
